import { useState } from "react";
import { ChartBar, CaretDown, CaretRight } from "@phosphor-icons/react";

const KIND_LABEL = { injury: "ICD-10-CM", procedure: "ICD-10-PCS", external: "External", ais: "AIS 2015" };

export default function CorrectionStats({ stats }) {
  const [open, setOpen] = useState(false);
  const kinds = Object.entries(stats?.by_kind || {});
  const maxIssues = Math.max(1, ...kinds.map(([, v]) => v.corrected + v.removed));

  return (
    <div className="border-t border-[#E5E0D8]" data-testid="correction-stats">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full px-4 pt-2 pb-2 flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-[#5C665A] hover:bg-[#EBE7DF] transition-colors"
        data-testid="correction-stats-toggle"
      >
        {open ? <CaretDown size={10} weight="bold" /> : <CaretRight size={10} weight="bold" />}
        <ChartBar size={12} weight="duotone" /> Correction stats
        <span className="ml-auto font-mono normal-case tracking-normal" data-testid="correction-stats-cases">
          {stats?.validated_cases || 0} cases
        </span>
      </button>
      {open && (
        <div className="px-3 pb-3 space-y-3 max-h-[24vh] overflow-y-auto tc-scroll" data-testid="correction-stats-body">
          {!stats?.validated_cases ? (
            <p className="text-[11px] italic text-[#5C665A] px-1">Save validated cases to see recurring corrections.</p>
          ) : (
            <>
              <div className="space-y-1.5">
                {kinds.map(([kind, v]) => (
                  <div key={kind} data-testid={`stats-kind-${kind}`}>
                    <div className="flex justify-between text-[11px] text-[#2C332A]">
                      <span className="font-semibold">{KIND_LABEL[kind]}</span>
                      <span className="font-mono text-[#5C665A]">
                        {v.corrected}c · {v.removed}r · {v.overridden}o / {v.total}
                      </span>
                    </div>
                    <div className="h-1.5 bg-[#EBE7DF] rounded mt-0.5 overflow-hidden flex">
                      <div className="bg-[#8C3A27]" style={{ width: `${(v.corrected / maxIssues) * 100}%` }} />
                      <div className="bg-[#D29063]" style={{ width: `${(v.removed / maxIssues) * 100}%` }} />
                    </div>
                  </div>
                ))}
                <p className="text-[10px] text-[#5C665A]">c = corrected · r = removed · o = coder overrides</p>
              </div>
              {stats.top_corrections.length > 0 && (
                <div>
                  <p className="text-[10px] uppercase tracking-[0.16em] font-semibold text-[#5C665A] mb-1">Top corrections</p>
                  {stats.top_corrections.map((c, i) => (
                    <div key={i} className="text-[11px] font-mono flex justify-between gap-1" data-testid={`top-correction-${i}`}>
                      <span className="truncate">
                        <span className="line-through text-[#8C3A27]">{c.from}</span> → {c.to}
                      </span>
                      <span className="text-[#5C665A]">×{c.count}</span>
                    </div>
                  ))}
                </div>
              )}
              {stats.top_reasons.length > 0 && (
                <div>
                  <p className="text-[10px] uppercase tracking-[0.16em] font-semibold text-[#5C665A] mb-1">Common reasons</p>
                  {stats.top_reasons.map((r, i) => (
                    <p key={i} className="text-[11px] text-[#2C332A] leading-snug mb-1" data-testid={`top-reason-${i}`}>
                      <span className="font-mono text-[#5C665A]">×{r.count}</span> {r.reason}
                    </p>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
